"use client";

import { Link } from "@/component/link";
import Logo from "@/component/logo";
import Slideshow from "@/component/slideshow";
import { useLanguage } from "@/context/language-context";
import { useAuth } from "@/controller/auth-controller";
import { useUserController } from "@/controller/user-controller";
import { getLocalizedPath } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { Config } from "@/shared/config";
import {
  Button,
  Card,
  CardBody,
  Checkbox,
  Divider,
  Form,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tab,
  Tabs
} from "@heroui/react";
import { IconBrandGoogle, IconEyeClosed, IconLink } from "@tabler/icons-react";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect } from "react";

export default function Auth() {
  const {
    data: userData,
    isLoading: isCheckingUser,
  } = useUserController({ ignoreException: true });
  const router = useRouter();
  const { language } = useLanguage();

  useEffect(() => {
    if (userData?.user) {
      router.push("/authenticated");
    }
  }, [router, userData?.user]);

  return (
    <>
      <style jsx>{`
        input, textarea, select {
          font-size: 16px !important;
        }
        @media (max-width: 768px) {
          input, textarea, select {
            font-size: 16px !important;
          }
        }
      `}</style>

      <div
        className={cn(
          "bg-[radial-gradient(hsl(var(--heroui-primary-800)_/_0.15)_10%,hsl(var(--heroui-primary)_/_0.0)_67%)]",
          "dark:bg-[radial-gradient(hsl(var(--heroui-primary-800)_/_0.1)_10%,hsl(var(--heroui-primary)_/_0.0)_67%)]",
          "min-h-screen"
        )}
        style={{
          backgroundPosition: "0 -150px",
          backgroundSize: "auto 1600px",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Mobile slideshow at top */}
        <div className="desktop:hidden w-full h-64 sm:h-64">
          <Slideshow />
        </div>

        <div className="min-h-screen w-full flex flex-col desktop:flex-row">
          <div id="content" className="w-full desktop:w-2/5 flex flex-col overflow-y-auto p-4 desktop:p-6 scrollbar-hide relative z-10">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4 sm:gap-0">
              <div className="flex justify-between w-full gap-3">
                <Logo />
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <Button
                  as={Link}
                  href={getLocalizedPath('/', language)}
                  target="_blank"
                  className="text-xs text-default-600 flex-shrink-0 flex hover:underline sm:w-auto"
                  radius="lg"
                  variant="light"
                  endContent={<IconLink size={16} />}
                >
                  Know more about VareseLab
                </Button>
              </div>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center">
              <Card className="w-full max-w-md">
                <CardBody className="flex flex-col w-full items-center justify-center gap-4 py-6">
                  <Tabs>
                    <Tab key="login" title="Login">
                      <Login isCheckingUser={isCheckingUser} />
                    </Tab>
                    <Tab key="register" title="Register">
                      <Register isCheckingUser={isCheckingUser} />
                    </Tab>
                  </Tabs>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>

        <div id="images" className="hidden desktop:block w-3/5 fixed right-0 top-0 h-screen z-0">
          <Slideshow />
        </div>
      </div>
    </>
  );
}

const Login = ({ isCheckingUser }: { isCheckingUser: boolean }) => {
  const [isVisible, setIsVisible] = React.useState(false);

  const toggleVisibility = () => setIsVisible(!isVisible);

  const authController = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] =
    React.useState(false);
  const [resetPassword, setResetPassword] = React.useState<string | null>(null);
  const [loadingResetPassword, setLoadingResetPassword] = React.useState(false);
  const [urlError, setUrlError] = React.useState<string | null>(null);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setUrlError(errorParam);
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      router.replace(url.pathname + url.search);
    }
  }, [searchParams, router]);

  return (
    <div className="flex w-full desktop:min-w-[400px] flex-col gap-4 px-8">
      <div className="flex flex-col gap-1">
        <span className="text-large font-medium">Sign in to your account</span>
        <p className="text-small text-default-500">
          to continue with {Config.projectName}
        </p>
      </div>

      <Modal isOpen={error !== null} onClose={() => setError(null)}>
        <ModalContent>
          <ModalHeader>Authentication</ModalHeader>
          <ModalBody>Login failed. Please try again.</ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setError(null)}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={urlError !== null} onClose={() => setUrlError(null)}>
        <ModalContent>
          <ModalHeader>Authentication Error</ModalHeader>
          <ModalBody>{urlError}</ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setUrlError(null)}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
      >
        <ModalContent>
          <ModalHeader>Forgot Password</ModalHeader>
          <ModalBody>
            <Input
              isRequired
              label="Email Address"
              name="email"
              placeholder="Enter your email"
              type="email"
              variant="bordered"
              value={resetPassword ?? undefined}
              onChange={(e) => setResetPassword(e.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              isDisabled={!resetPassword}
              isLoading={loadingResetPassword}
              onPress={() => {
                if (resetPassword) {
                  setLoadingResetPassword(true);

                  authController
                    .resetPassword(resetPassword)
                    .then(() => {
                      setIsResetPasswordModalOpen(false);
                    })
                    .finally(() => {
                      setLoadingResetPassword(false);
                    });
                }
              }}
              aria-label="Send password reset email"
            >
              Send reset email
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Form
        className="flex flex-col gap-3"
        validationBehavior="native"
        onSubmit={async (event) => {
          event.preventDefault();

          setIsLoading(true);

          const formData = new FormData(event.target as HTMLFormElement);

          const email = formData.get("email") as string;
          const password = formData.get("password") as string;

          await authController
            .login(email, password)
            .then(() => {
              router.push("/authenticated");
            })
            .catch((error) => {
              setError(error.message);
            })
            .finally(() => {
              setIsLoading(false);
            });
        }}
      >
        <Input
          isRequired
          label="Email Address"
          name="email"
          placeholder="Enter your email"
          type="email"
          variant="bordered"
        />
        <Input
          isRequired
          endContent={
            <button
              type="button"
              onClick={toggleVisibility}
              aria-label="Toggle password visibility"
            >
              {isVisible ? (
                <IconEyeClosed
                  className="pointer-events-none text-2xl text-default-400"
                  aria-label="Hide password"
                  role="img"
                />
              ) : (
                <IconEyeClosed
                  className="pointer-events-none text-2xl text-default-400"
                  aria-label="Show password"
                  role="img"
                />
              )}
            </button>
          }
          label="Password"
          name="password"
          placeholder="Enter your password"
          type={isVisible ? "text" : "password"}
          variant="bordered"
        />

        <Button
          className="w-full"
          color="primary"
          type="submit"
          isLoading={isLoading || isCheckingUser}
          aria-label="Sign in to your account"
        >
          Sign In
        </Button>
      </Form>

      <div className="flex justify-end">
        <Link
          size="sm"
          onPress={() => setIsResetPasswordModalOpen(true)}
          aria-label="Forgot password?"
          className="cursor-pointer"
        >
          Forgot password?
        </Link>
      </div>

      <div className="flex items-center gap-4 py-2">
        <Divider className="flex-1" />
        <span className="text-sm text-default-500">OR</span>
        <Divider className="flex-1" />
      </div>

      <Button
        className="w-full"
        variant="bordered"
        startContent={<IconBrandGoogle size={18} />}
        onPress={() => authController.googleLogin()}
        aria-label="Sign in with Google"
      >
        Sign in with Google
      </Button>

      <AgreementSignIn />
    </div>
  );
};

const Register = ({ isCheckingUser }: { isCheckingUser: boolean }) => {
  const [isVisible, setIsVisible] = React.useState(false);

  const toggleVisibility = () => setIsVisible(!isVisible);

  const authController = useAuth();
  const router = useRouter();

  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isChecked, setIsChecked] = React.useState(false);
  return (
    <div className="flex w-full desktop:min-w-[400px] flex-col gap-4 px-8">
      <div className="flex flex-col gap-1">
        <span className="text-large font-medium">Create an account</span>
        <p className="text-small text-default-500">
          to continue to {Config.projectName}
        </p>
      </div>

      <Modal isOpen={error !== null} onClose={() => setError(null)}>
        <ModalContent>
          <ModalHeader>Authentication</ModalHeader>
          <ModalBody>{error ?? "Login failed. Please try again."}</ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={() => setError(null)}
              aria-label="Close error message"
            >
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Form
        className="flex flex-col gap-3"
        validationBehavior="native"
        onSubmit={async (event) => {
          event.preventDefault();

          setIsLoading(true);

          const formData = new FormData(event.target as HTMLFormElement);

          const email = formData.get("email") as string;
          const password = formData.get("password") as string;
          const confirmPassword = formData.get("confirmPassword") as string;

          if (password !== confirmPassword) {
            setError("Passwords do not match");
            setIsLoading(false);
            return;
          }

          await authController
            .signup(email, password, confirmPassword)
            .then(() => {
              router.push("/authenticated");
            })
            .catch((error) => {
              setError(error.message);
            })
            .finally(() => {
              setIsLoading(false);
            });
        }}
      >
        <Input
          isRequired
          label="Email Address"
          name="email"
          placeholder="Enter your email"
          type="email"
          variant="bordered"
        />
        <Input
          isRequired
          endContent={
            <button
              type="button"
              onClick={toggleVisibility}
              aria-label="Toggle password visibility"
            >
              {isVisible ? (
                <IconEyeClosed
                  className="pointer-events-none text-2xl text-default-400"
                  aria-label="Hide password"
                  role="img"
                />
              ) : (
                <IconEyeClosed
                  className="pointer-events-none text-2xl text-default-400"
                  aria-label="Show password"
                  role="img"
                />
              )}
            </button>
          }
          label="Password"
          name="password"
          placeholder="Enter your password"
          type={isVisible ? "text" : "password"}
          variant="bordered"
        />
        <Input
          isRequired
          endContent={
            <button
              type="button"
              onClick={toggleVisibility}
              aria-label="Toggle password visibility"
            >
              {isVisible ? (
                <IconEyeClosed
                  className="pointer-events-none text-2xl text-default-400"
                  aria-label="Hide password"
                  role="img"
                />
              ) : (
                <IconEyeClosed
                  className="pointer-events-none text-2xl text-default-400"
                  aria-label="Show password"
                  role="img"
                />
              )}
            </button>
          }
          label="Password"
          name="confirmPassword"
          placeholder="Confirm your password"
          type={isVisible ? "text" : "password"}
          variant="bordered"
        />

        <AgreementSignup isChecked={isChecked} setIsChecked={setIsChecked} />

        <Button
          className="w-full"
          color="primary"
          type="submit"
          isLoading={isLoading || isCheckingUser}
          isDisabled={!isChecked}
          aria-label="Create new account"
        >
          Sign Up
        </Button>

        <div className="flex items-center gap-4 py-2 w-full">
          <Divider className="flex-1" />
          <span className="text-sm text-default-500">OR</span>
          <Divider className="flex-1" />
        </div>

        <Button
          className="w-full"
          variant="bordered"
          isDisabled={!isChecked}
          startContent={<IconBrandGoogle size={18} />}
          onPress={() => authController.googleSignup()}
          aria-label="Sign up with Google"
        >
          Sign up with Google
        </Button>
      </Form>
    </div>
  );
};

type AgreementProps = {
  isChecked: boolean;
  setIsChecked: (isChecked: boolean) => void;
}

const AgreementSignup = ({ isChecked, setIsChecked }: AgreementProps) => {
  return (
    <div className="flex flex-row gap-1 py-2">
      <Checkbox
        isSelected={isChecked}
        onValueChange={setIsChecked}
        isRequired
        size="sm"
      />
      <div className="block text-sm">
        <span>I agree with the </span>
        <Link
          href="/docs/terms-of-service"
          target="_blank"
          size="sm"
        >
          Terms
        </Link>
        <span> and </span>
        <Link
          href="/docs/privacy-policy"
          target="_blank"
          size="sm"
        >
          Privacy Policy
        </Link>
      </div>
    </div>
  )
}

const AgreementSignIn = () => {
  return (
    <div className="flex flex-row text-center gap-1 py-2">
      <div className="block text-sm">
        <span>By signing in, you agree to our </span>
        <Link
          href="/docs/terms-of-service"
          target="_blank"
          size="sm"
        >
          Terms
        </Link>
        <span> and </span>
        <Link
          href="/docs/privacy-policy"
          target="_blank"
          size="sm"
        >
          Privacy Policy
        </Link>
      </div>
    </div>
  )
}
